import { useTranslation } from "react-i18next"
import {
  PlugZap,
  CircleCheck,
  TriangleAlert,
  Loader2,
  CircleX,
  Unplug,
  CircleHelp,
} from "lucide-react"

import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { ConnectionStatus } from "@/types"

interface IconProps {
  component: React.ComponentType<React.SVGProps<SVGSVGElement>>
  style?: string
}

const iconMap: Record<string, IconProps> = {
  [ConnectionStatus.ScopesInitializing]: {
    component: PlugZap,
    style: "animate-breathe",
  },
  [ConnectionStatus.NotConnected]: {
    component: Unplug,
    style: "text-gray-500",
  },
  [ConnectionStatus.NotImported]: {
    component: TriangleAlert,
    style: "text-yellow-500",
  },
  [ConnectionStatus.InProgress]: {
    component: Loader2,
    style: "animate-spin-slow",
  },
  [ConnectionStatus.ImportFailed]: {
    component: CircleX,
    style: "text-red-500",
  },
  [ConnectionStatus.ImportDone]: {
    component: CircleCheck,
    style: "text-green-500",
  },
  [ConnectionStatus.Connected]: {
    component: CircleCheck,
    style: "text-green-500",
  },
  Unknown: {
    component: CircleHelp,
    style: "text-gray-500",
  },
}

export interface ConnectionStatusIconProps {
  className?: string
  status: string
  showBadge?: boolean
}

const ConnectionStatusIcon = ({
  status,
  className,
  showBadge = false,
}: ConnectionStatusIconProps) => {
  const { t } = useTranslation()
  const displayStatus = status in iconMap ? status : "Unknown"
  const { component: Icon, style } = iconMap[displayStatus]

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center">
            <Icon className={cn("mr-2 h-4 w-4", style, className)} />
            {showBadge && (
              <Badge
                variant={
                  status === ConnectionStatus.ImportFailed
                    ? "destructive"
                    : "secondary"
                }
              >
                {t(`types.connectionStatus.${displayStatus}`)}
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t(`types.connectionStatus.${status}`)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default ConnectionStatusIcon
